import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApplicationContext } from '@/context/v1/Application/context';
import type { Context, Resolution886FileFilter, UrlFilters } from './types';

interface Resolution886ReportFilesProviderProps {
	children: JSX.Element | JSX.Element[];
	queryFilters: UrlFilters;
}

const Resolution886ReportFilesContext = createContext<Context>({} as Context);

export function Resolution886ReportFilesProvider({
	children,
	queryFilters
}: Readonly<Resolution886ReportFilesProviderProps>) {
	const { id = '' } = useParams<{ id: string }>();
	const { organization, company } = useApplicationContext();

	const organization_id = queryFilters.organization_id;
	const company_id = queryFilters.company_id;
	const sector_id = queryFilters.sector_id;
	const line_id = queryFilters.line_id;
	const workstation_id = queryFilters.workstation_id;
	const original_name = queryFilters.original_name;
	const start_date = queryFilters.start_date;
	const end_date = queryFilters.end_date;
	const offset = queryFilters.offset;
	const limit = queryFilters.limit;

	const initialFilters = {
		organization_id: organization_id || organization?.id,
		company_id: company_id || company?.id,
		start_date: start_date ? new Date(start_date.toString()) : undefined,
		end_date: end_date ? new Date(end_date.toString()) : undefined,
		sector_id,
		line_id,
		workstation_id,
		original_name,
		offset,
		limit
	};

	const [extractionId, setExtractionId] = useState<string>(id);
	const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(false);
	const [filters, setFilters] = useState<Resolution886FileFilter>(initialFilters);
	const [deselectedFiles, setDeselectedFiles] = useState<Set<string>>(new Set());

	const handleSetFilter = useCallback((data?: Partial<Resolution886FileFilter>): void => {
		setFilters((prevState: Resolution886FileFilter) => ({ ...prevState, ...data }));
	}, []);

	const handleResetFilter = useCallback((): void => {
		setFilters(initialFilters);
	}, [initialFilters]);

	const handleOnChangePage = useCallback(
		(offset: number, limit: number): void => {
			handleSetFilter({ offset, limit });
		},
		[handleSetFilter]
	);

	const handleSetExtractionId = useCallback((id: string): void => {
		setExtractionId(id);
	}, []);

	const handleSetDeselectedFiles = useCallback((files: Set<string>): void => {
		setDeselectedFiles(files);
	}, []);

	const handleSetSelectAll = useCallback((value: boolean): void => {
		setIsSelectAllChecked(value);
	}, []);

	const values = useMemo(
		(): Context => ({
			filters,
			extractionId,
			deselectedFiles,
			isSelectAllChecked,
			onSetFilter: handleSetFilter,
			onResetFilter: handleResetFilter,
			onChangePage: handleOnChangePage,
			onSetSelectAll: handleSetSelectAll,
			onSetExtractionId: handleSetExtractionId,
			onSetDeselectedFiles: handleSetDeselectedFiles
		}),
		[
			filters,
			extractionId,
			deselectedFiles,
			isSelectAllChecked,
			handleSetFilter,
			handleResetFilter,
			handleOnChangePage,
			handleSetSelectAll,
			handleSetExtractionId,
			handleSetDeselectedFiles
		]
	);

	return (
		<Resolution886ReportFilesContext.Provider value={values}>{children}</Resolution886ReportFilesContext.Provider>
	);
}

export function useResolution886ReportFile(): Context {
	const context = useContext(Resolution886ReportFilesContext);

	if (!context) {
		throw new Error('useResolution886ReportFiles must be used within a Resolution886ReportFilesProvider');
	}

	return context;
}
